import { useEffect, useMemo, useState } from "react";
import {
    Heart, MapPin, PaperPlaneTilt, Suitcase, Check,
    Calendar, Users, Airplane, GlobeHemisphereWest, Clock, Ticket,
} from "@phosphor-icons/react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import PassportAppHeader from "@/components/PassportAppHeader";
import DiscoverRatingStars from "@/components/discover/DiscoverRatingStars";
import DiscoverEmptyState from "@/components/discover/DiscoverEmptyState";
import { useAuth } from "@/context/AuthContext";
import { discoverApi, toAbsolute } from "@/lib/apiClient";
import { usePassportShare } from "@/components/messages/PassportShareProvider";
import {
    KIND_ICONS,
    KIND_COLORS,
    TripItemCard,
    BudgetCard,
    MetaChip,
    StatusPill,
    sumItems,
} from "@/pages/TripDashboard";
import { TierBadge } from "@/pages/TravelDate";
import {
    ItineraryList,
    ScoreCard,
    BudgetFitBadge,
    CORAL,
} from "@/pages/TravelDateDetail";

function formatCompact(value) {
    return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(value || 0);
}

export default function DiscoverPlanDetail({ kind = "trip" }) {
    const { id } = useParams();
    const { user } = useAuth() || {};
    const { openPassportShare } = usePassportShare();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = async (showLoading = false) => {
        if (showLoading) setLoading(true);
        try {
            const response = kind === "trip" ? await discoverApi.tripDetail(id) : await discoverApi.dateDetail(id);
            setDetail(response);
        } catch {
            if (showLoading) setDetail(null);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    useEffect(() => {
        load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, kind]);

    const plan = detail?.[kind === "trip" ? "trip" : "traveldate"];
    const creatorHref = detail?.owner?.handle ? `/profile/${detail.owner.handle}` : "/app";
    const destinationLabel = detail?.destination?.destination_label || [detail?.destination?.city_name, detail?.destination?.country_name].filter(Boolean).join(", ");
    const isOwner = Boolean(
        detail?.viewer?.is_owner ||
        (user?.id && detail?.owner?.user_id === user.id)
    );
    const ownerDashboardHref = kind === "trip" ? `/trip/${id}` : `/traveldate/${id}`;
    const hasAdded = Boolean(detail?.viewer?.has_added);
    const addedTargetId = detail?.viewer?.added_target_id;
    const addedHref = kind === "trip"
        ? (addedTargetId ? `/trip/${addedTargetId}` : "/concierge")
        : (addedTargetId ? `/traveldate/${addedTargetId}` : "/traveldate");

    const submitLike = async () => {
        if (!user) return toast.info("Sign in to like community plans.");
        const prevLiked = Boolean(detail?.viewer?.liked);
        const nextLiked = !prevLiked;
        const delta = nextLiked ? 1 : -1;

        // Optimistically update like state and counter
        setDetail((prev) => {
            if (!prev) return prev;
            const currentCount = Number(prev?.metrics?.likes_count || 0);
            return {
                ...prev,
                viewer: {
                    ...prev.viewer,
                    liked: nextLiked,
                },
                metrics: {
                    ...prev.metrics,
                    likes_count: Math.max(0, currentCount + delta),
                },
            };
        });

        try {
            const updater = kind === "trip" ? discoverApi.likeTrip : discoverApi.likeDate;
            await updater(id, nextLiked);
            await load(false);
        } catch (error) {
            // Revert state on failure
            setDetail((prev) => {
                if (!prev) return prev;
                const currentCount = Number(prev?.metrics?.likes_count || 0);
                return {
                    ...prev,
                    viewer: { ...prev.viewer, liked: prevLiked },
                    metrics: { ...prev.metrics, likes_count: Math.max(0, currentCount - delta) },
                };
            });
            toast.error(error?.response?.data?.detail || "Could not update likes.");
        }
    };

    const submitRating = async (rating) => {
        if (!user) return toast.info("Sign in to rate community plans.");
        const prevRating = detail?.viewer?.rating || 0;

        // Optimistically update rating in UI
        setDetail((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                viewer: {
                    ...prev.viewer,
                    rating,
                },
            };
        });

        try {
            const updater = kind === "trip" ? discoverApi.rateTrip : discoverApi.rateDate;
            await updater(id, rating);
            toast.success("Rating saved.");
            await load(false);
        } catch (error) {
            setDetail((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    viewer: { ...prev.viewer, rating: prevRating },
                };
            });
            toast.error(error?.response?.data?.detail || "Could not save your rating.");
        }
    };

    const clearRating = async () => {
        if (!user) return;
        const prevRating = detail?.viewer?.rating || 0;

        setDetail((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                viewer: {
                    ...prev.viewer,
                    rating: 0,
                },
            };
        });

        try {
            const updater = kind === "trip" ? discoverApi.clearTripRating : discoverApi.clearDateRating;
            await updater(id);
            toast.success("Rating cleared.");
            await load(false);
        } catch {
            setDetail((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    viewer: { ...prev.viewer, rating: prevRating },
                };
            });
            toast.error("Could not clear your rating.");
        }
    };

    const submitAdd = async () => {
        if (!user) return toast.info("Sign in to add this plan.");
        if (detail?.viewer?.has_added) {
            return toast.info(kind === "trip" ? "This trip is already in your dashboard." : "This date is already in your dashboard.");
        }
        setBusy(true);
        try {
            const key = `${kind}-${id}-${user.id}`;
            const updater = kind === "trip" ? discoverApi.addTripToItinerary : discoverApi.addDateToMyDates;
            const res = await updater(id, key);
            const addedTargetId = res?.created_trip_id || res?.created_date_id;
            setDetail((prev) => prev ? {
                ...prev,
                viewer: {
                    ...prev.viewer,
                    has_added: true,
                    added_target_id: addedTargetId || prev.viewer?.added_target_id,
                    can_add: false,
                },
                metrics: {
                    ...prev.metrics,
                    additions_count: (prev.metrics?.additions_count || 0) + (res?.already_added ? 0 : 1),
                },
            } : prev);
            toast.success(res?.already_added 
                ? (kind === "trip" ? "Already in My Trips" : "Already in My Dates")
                : (kind === "trip" ? "Added to My Trips" : "Added to My Dates")
            );
            await load(false);
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not add this plan.");
        } finally {
            setBusy(false);
        }
    };

    const groupedItems = useMemo(() => {
        if (kind !== "trip") return {};
        const map = {};
        for (const it of plan?.items || []) {
            const k = it.kind || "activity";
            if (!map[k]) map[k] = [];
            map[k].push(it);
        }
        return map;
    }, [plan?.items, kind]);

    const totals = useMemo(() => {
        if (kind !== "trip" || !plan) return null;
        if (plan.totals) return plan.totals;
        const total_spent = sumItems(plan.items || []);
        return {
            total_spent,
            budget_total: plan.budget || null,
            remaining: plan.budget ? plan.budget - total_spent : null,
            over_budget: plan.budget ? total_spent > plan.budget : false,
            by_category: {},
        };
    }, [plan, kind]);

    const stats = useMemo(() => ([
        { label: "Likes", value: formatCompact(detail?.metrics?.likes_count) },
        { label: "Rating", value: detail?.metrics?.ratings_count ? `${detail.metrics.ratings_avg.toFixed(1)}★` : "New" },
        { label: "Added", value: formatCompact(detail?.metrics?.additions_count) },
    ]), [detail]);

    if (loading) {
        return <div className="min-h-screen grid place-items-center text-white/60">Loading plan…</div>;
    }

    if (!detail || !plan) {
        return (
            <div className="min-h-screen grain text-white">
                <PassportAppHeader homeHref="/discover" subtitle="Discover detail" maxWidthClass="max-w-[1200px]" />
                <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16">
                    <DiscoverEmptyState title="This community plan isn’t public right now" description="It may have been removed, unpublished, or hidden by privacy settings." ctaLabel="Back to Discover" ctaHref="/discover" testId="discover-detail-empty" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen grain text-white" data-testid="discover-detail-view">
            <PassportAppHeader homeHref="/discover" subtitle={kind === "trip" ? "Community Trip" : "Community Date"} maxWidthClass="max-w-[1200px]" />

            <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-8 space-y-8">
                {/* Hero */}
                <div className="rounded-3xl overflow-hidden relative aspect-[16/9] md:aspect-[16/7]" style={{ border: "1px solid rgba(255,255,255,0.06)" }} data-testid="discover-hero">
                    {plan.hero_image ? (
                        <img src={toAbsolute(plan.hero_image)} alt={plan.title} className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full" style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.16), rgba(42,67,101,0.24))" }} />
                    )}
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,19,32,0.2) 0%, rgba(5,10,17,0.92) 100%)" }} />

                    <div className="relative h-full p-6 md:p-10 flex flex-col justify-end">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                            {kind === "trip" ? (
                                <StatusPill status={plan.status || "planning"} />
                            ) : (
                                <>
                                    {plan.tier ? <TierBadge tier={plan.tier} /> : null}
                                    {plan.score ? (
                                        <span className="rounded-full px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-medium" style={{ background: "rgba(11,19,32,0.7)", color: "#F4A28F", border: "1px solid rgba(244,162,143,0.4)" }}>
                                            Score {plan.score}
                                        </span>
                                    ) : null}
                                </>
                            )}
                            <span className="text-[10px] tracking-[0.3em] uppercase text-white/80">
                                {kind === "trip" ? "Community Trip" : "Community TravelDate"}
                            </span>
                        </div>

                        <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl text-white leading-[1] tracking-tight" data-testid="discover-detail-title">
                            {kind === "trip" ? `${plan.emoji || "✈️"} ` : "❤️ "}{plan.title}
                        </h1>

                        <div className="mt-3 flex flex-wrap gap-3 text-white/85 text-sm">
                            {kind === "trip" ? (
                                <>
                                    {plan.days ? <MetaChip icon={Calendar} label={`${plan.days} ${plan.days === 1 ? "day" : "days"}`} /> : null}
                                    <MetaChip icon={Users} label={`${((plan.travelers || {}).adults || 1) + ((plan.travelers || {}).children || 0)} travelers`} />
                                    {plan.origin ? <MetaChip icon={Airplane} label={`From ${plan.origin}`} /> : null}
                                    <MetaChip icon={GlobeHemisphereWest} label={(plan.destinations || []).slice(0, 3).join(" → ") || destinationLabel} />
                                </>
                            ) : (
                                <>
                                    <span className="inline-flex items-center gap-1.5"><MapPin size={13} weight="fill" color="#F4A28F" /> {plan.city || destinationLabel}{plan.country ? `, ${plan.country}` : ""}</span>
                                    {plan.duration_hours ? <span className="inline-flex items-center gap-1.5"><Clock size={13} weight="fill" color="#F4A28F" /> {plan.duration_hours}h</span> : null}
                                    {plan.vibe ? <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs" style={{ background: "rgba(244,162,143,0.12)", color: "#F4A28F" }}>{plan.vibe}</span> : null}
                                </>
                            )}
                        </div>

                        <div className="flex flex-wrap gap-3 mt-6">
                            <button type="button" onClick={submitLike} className="btn-ghost text-sm !py-2.5 !px-4" data-testid="discover-detail-like-button">
                                <Heart size={14} weight={detail.viewer?.liked ? "fill" : "regular"} /> {detail.viewer?.liked ? "Liked" : "Like"}
                            </button>
                            {!isOwner ? (
                                hasAdded ? (
                                    <Link
                                        to={addedHref}
                                        className="text-sm !py-2.5 !px-4 inline-flex items-center gap-2 rounded-full font-medium transition-all"
                                        style={{ background: "rgba(159,213,208,0.15)", color: "#9FD5D0", border: "1px solid rgba(159,213,208,0.4)" }}
                                        data-testid="discover-detail-added-btn"
                                    >
                                        <Check size={14} weight="bold" /> {kind === "trip" ? "Added to My Trips · View" : "Added to My Dates · View"}
                                    </Link>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={submitAdd}
                                        disabled={busy}
                                        className="btn-gold text-sm !py-2.5 !px-4 disabled:opacity-60"
                                        data-testid="discover-detail-add-cta"
                                    >
                                        <Suitcase size={14} weight="fill" /> {busy ? "Adding…" : (kind === "trip" ? "Add to My Trips" : "Add to My Dates")}
                                    </button>
                                )
                            ) : (
                                <Link to={ownerDashboardHref} className="btn-gold text-sm !py-2.5 !px-4 inline-flex items-center gap-2" data-testid="discover-detail-manage-btn">
                                    <Suitcase size={14} weight="fill" /> {kind === "trip" ? "Edit in Trip Dashboard" : "Open in TravelDate"}
                                </Link>
                            )}
                            <button type="button" onClick={() => openPassportShare({ shareRef: { entity_type: kind === "trip" ? "trip" : "traveldate", entity_id: id }, sourceLabel: kind === "trip" ? "this Trip" : "this TravelDate" })} className="btn-ghost text-sm !py-2.5 !px-4" data-testid="discover-detail-share-button">
                                <PaperPlaneTilt size={14} /> Share
                            </button>
                        </div>
                    </div>
                </div>

                {/* Layout: Main Items Column + Sidebar */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
                    <div>
                        {kind === "trip" ? (
                            <>
                                {!!plan.booking_summary?.length && (
                                    <div className="mb-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="trip-booking-summary">
                                        {plan.booking_summary.map((entry) => (
                                            <div key={entry.kind} className="rounded-2xl p-4" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                                                <div className="text-sm text-white font-medium">{entry.icon} {entry.label}</div>
                                                <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 mt-2">{entry.count} {entry.count === 1 ? "item" : "items"}</div>
                                                <div className="mt-2 text-[11px] inline-flex px-2 py-1 rounded-full" style={{ background: entry.status_type === "estimated" ? "rgba(212,175,55,0.12)" : "rgba(159,213,208,0.12)", color: entry.status_type === "estimated" ? "var(--gold)" : "#9FD5D0", border: `1px solid ${entry.status_type === "estimated" ? "rgba(212,175,55,0.3)" : "rgba(159,213,208,0.3)"}` }}>
                                                    {entry.status}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {Object.keys(KIND_ICONS).map((k) => {
                                    const items = groupedItems[k] || [];
                                    if (items.length === 0) return null;
                                    const Icon = KIND_ICONS[k] || Ticket;
                                    const color = KIND_COLORS[k] || "#D4AF37";
                                    const label = k.charAt(0).toUpperCase() + k.slice(1);
                                    return (
                                        <section key={k} className="mb-8" data-testid={`discover-trip-section-${k}`}>
                                            <div className="flex items-center gap-2 mb-3">
                                                <Icon size={16} color={color} weight="fill" />
                                                <h3 className="font-serif text-2xl text-white leading-none">{label}</h3>
                                                <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 ml-1">
                                                    {items.length} · ${sumItems(items).toLocaleString()}
                                                </span>
                                            </div>
                                            <div className="space-y-3">
                                                {items.map((it) => (
                                                    <TripItemCard key={it.id || it._id || Math.random()} item={it} />
                                                ))}
                                            </div>
                                        </section>
                                    );
                                })}

                                {(plan.items || []).length === 0 && (
                                    <div className="rounded-3xl p-8 text-center text-white/60" style={{ background: "var(--navy-deep)", border: "1px dashed rgba(255,255,255,0.1)" }}>
                                        No items added to this trip yet.
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="space-y-6">
                                {plan.vibe && (
                                    <div className="text-lg text-white/85 italic">"{plan.vibe}"</div>
                                )}

                                {plan.why_this_works && (
                                    <div className="rounded-3xl p-6" style={{ background: "var(--navy-deep)", border: `1px solid ${CORAL}33` }}>
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#F4A28F] mb-2">Why this works</div>
                                        <p className="text-sm text-white/80 leading-relaxed">{plan.why_this_works}</p>
                                    </div>
                                )}

                                <div>
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-white/40 mb-4">Date Itinerary</div>
                                    <ItineraryList d={plan} />
                                </div>

                                {plan.estimated_cost_usd != null && (
                                    <div className="rounded-2xl p-4 flex items-center justify-between" style={{ background: "var(--navy-deep)", border: `1px solid ${CORAL}22` }} data-testid="td-total-cost">
                                        <div>
                                            <div className="text-[10px] tracking-[0.2em] uppercase text-white/50">Total Est. Cost</div>
                                            <div className="font-serif text-2xl text-white mt-1">${plan.estimated_cost_usd}</div>
                                        </div>
                                        <BudgetFitBadge date={plan} />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Sidebar with self-start (prevents CSS grid stretching) */}
                    <div className="space-y-6 self-start lg:sticky lg:top-24">
                        {kind === "trip" && totals ? (
                            <BudgetCard totals={totals} />
                        ) : null}

                        {kind === "date" && plan.score ? (
                            <ScoreCard d={plan} />
                        ) : null}

                        {/* Creator Card & Social Rating */}
                        <aside className="rounded-3xl p-5 sm:p-6" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.08)" }}>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-white/38 mb-4">Creator</div>
                            <Link to={creatorHref} className="flex items-center gap-3" data-testid="discover-detail-creator-link">
                                <img src={toAbsolute(detail.owner?.avatar_url) || `https://api.dicebear.com/7.x/notionists/svg?seed=${detail.owner?.handle || "passport"}`} alt={detail.owner?.name || detail.owner?.handle} className="w-14 h-14 rounded-full object-cover ring-1 ring-white/10" />
                                <div>
                                    <div className="text-lg font-medium">{detail.owner?.name || detail.owner?.handle}</div>
                                    <div className="text-sm text-white/45">@{detail.owner?.handle}</div>
                                </div>
                            </Link>

                            <div className="grid grid-cols-3 gap-2 mt-6" data-testid="discover-detail-metrics">
                                {stats.map((stat) => (
                                    <div key={stat.label} className="rounded-2xl px-3 py-3" style={{ background: "rgba(255,255,255,0.04)" }}>
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-white/38">{stat.label}</div>
                                        <div className="text-sm mt-1 font-semibold">{stat.value}</div>
                                    </div>
                                ))}
                            </div>

                            {!isOwner ? (
                                <div className="mt-6" id="rating-section">
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/38 mb-2">Rate this plan</div>
                                    <DiscoverRatingStars value={detail.viewer?.rating || detail.metrics?.ratings_avg || 0} onRate={submitRating} size={18} testIdPrefix="discover-detail-rating" />
                                    {detail.viewer?.rating ? <button type="button" onClick={clearRating} className="text-xs text-white/55 mt-3 underline underline-offset-4" data-testid="discover-detail-clear-rating">Clear my rating</button> : null}
                                </div>
                            ) : (
                                <div className="mt-6 pt-4 border-t border-white/10 text-xs text-white/45 italic">
                                    You created this {kind === "trip" ? "trip" : "travel date"}.
                                </div>
                            )}
                        </aside>

                        <div className="rounded-3xl p-5" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-white/38 mb-2">Why it matters</div>
                            <p className="text-xs text-white/60 leading-relaxed">
                                This is a real Passport community plan. Adding it creates your own editable copy in your dashboard without spending AI credits.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}